import { AxiosRequestConfig, AxiosResponse } from 'axios'

import type { ApiClient } from 'clients/yogi-api'
import type {
  Condition,
  FieldCatalogResponse,
  ResultPage,
  ResultRow,
} from 'features/charts-v2/types/yogiql'
import type { FilterOptionsV2Payload } from 'features/filters/service/fetch-filter-options-v2'
import type {
  ProjectOptionsResponse,
  YogiQLFilterPreset,
} from 'features/filters/types'
import type {
  YogiQLFilterOptionsRequest,
  YogiQLFilterOptionsResult,
} from 'features/filters/utils/yogiql-filter-control'
const FILTER_OPTIONS_V2_URL = '/post/filter/options/v2'
const YOGIQL_FILTER_OPTIONS_URL = '/client/yogiql-filter-options'
const FIELD_CATALOG_URL = '/client/field-catalog'
const HUB_UUID_HEADER = 'X-Hub-UUID'
const PROJECT_SETTINGS_FILTER_PRESET = '/project/filter-preset'
const YOGIQL_FILTER_PRESETS_URL = '/client/filter-presets/yogiql'
import { checkYogiQLVersion, withYogiQLVersion } from 'features/charts-v2/utils'
import type { FilterPreset } from 'types/project'

/** The saved-filter lists' react-query key. The YogiQL list's extends the
 *  legacy one, so a refetch of the legacy key refreshes both. */
export const filterPresetsQueryKey = (
  projectId: string,
  yogiql = false,
): string[] =>
  yogiql
    ? ['filter-presets', projectId, 'yogiql']
    : ['filter-presets', projectId]

export class FilterService {
  constructor(private client: ApiClient) {}

  async getFilterOptionsV2(
    proj_uuid: string,
    body: FilterOptionsV2Payload,
    customParams?: Omit<AxiosRequestConfig, 'params'>,
  ): Promise<ProjectOptionsResponse> {
    let config: AxiosRequestConfig = { params: { proj_uuid } }
    if (customParams) config = { ...config, ...customParams }
    const { data } = await this.client.post<ProjectOptionsResponse>(
      FILTER_OPTIONS_V2_URL,
      body,
      config,
    )
    return data
  }

  /** One filter control's counted values (or a slider's range) as YogiQL rows. */
  async getYogiQLFilterOptions(
    proj_uuid: string,
    body: YogiQLFilterOptionsRequest,
    customParams?: Omit<AxiosRequestConfig, 'params'>,
  ): Promise<YogiQLFilterOptionsResult> {
    const { data } = await this.client.post<{
      data: ResultRow[]
      page: ResultPage
    }>(YOGIQL_FILTER_OPTIONS_URL, body, {
      headers: { [HUB_UUID_HEADER]: proj_uuid },
      ...customParams,
    })
    return { rows: data.data, page: data.page }
  }

  getFieldCatalog(proj_uuid: string): Promise<FieldCatalogResponse> {
    return this.client
      .get<FieldCatalogResponse>(FIELD_CATALOG_URL, {
        headers: { [HUB_UUID_HEADER]: proj_uuid },
      })
      .then(({ data }) => checkYogiQLVersion(data))
  }

  deleteFilterPreset(
    proj_uuid: string,
    preset_name: string,
  ): Promise<AxiosResponse<ProjectOptionsResponse>> {
    return this.client.delete(PROJECT_SETTINGS_FILTER_PRESET, {
      params: { proj_uuid, preset_name },
    })
  }

  async loadFilterPresets(proj_uuid: string): Promise<FilterPreset[]> {
    const { filter_presets } = await this.client
      .get<{
        filter_presets: FilterPreset[]
      }>(PROJECT_SETTINGS_FILTER_PRESET, { params: { proj_uuid } })
      .then(({ data }) => data)
    return filter_presets
  }

  /** The member's saved filters read as YogiQL, legacy ones translated. */
  async loadYogiQLFilterPresets(
    proj_uuid: string,
  ): Promise<YogiQLFilterPreset[]> {
    const { data } = await this.client.get<{ presets: YogiQLFilterPreset[] }>(
      YOGIQL_FILTER_PRESETS_URL,
      { headers: { [HUB_UUID_HEADER]: proj_uuid } },
    )
    return data.presets
  }

  /** Saves a new preset under the flag, themes and datasets by name. */
  async saveYogiQLFilterPreset(
    proj_uuid: string,
    name: string,
    filters: Condition[],
  ): Promise<void> {
    await this.client.post(
      YOGIQL_FILTER_PRESETS_URL,
      { name, query: withYogiQLVersion({ filters }) },
      { headers: { [HUB_UUID_HEADER]: proj_uuid } },
    )
  }
}
